/**
 * Sa3ee motion config – Premium professionalism & high-end dashboard stagger.
 * - List stagger: 0.1s start, 0.03–0.05s interval, cap for long lists (>10).
 * - Fade + slide (opacity 0, y: 15 → 1, 0), snappy ease [0.22, 1, 0.36, 1], 0.2–0.3s per item.
 */

export const easeOut = [0.4, 0, 0.2, 1] as const;

/** Snappy ease for list entry – feels responsive and smooth */
export const easeSnappy = [0.22, 1, 0.36, 1] as const;

export const tFast = {
  duration: 0.25,
  ease: easeOut,
};

export const tUi = {
  duration: 0.3,
  ease: easeOut,
};

export const springSidebar = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

/** Sign-up flow: slide & fade. 300–400ms. */
export const signUpTransition = {
  duration: 0.35,
  ease: [0.4, 0, 0.2, 1] as const,
};

/** Expandable fields (FAL, IBAN, Email): height 400ms ease-in-out for card “breathing” feel. */
export const accordionHeightTransition = {
  duration: 0.4,
  ease: [0.42, 0, 0.58, 1] as const, // ease-in-out
};

/** Content fade: 200ms (Settings accordion open/close). */
export const accordionContentFadeDuration = 0.2;

/** Content inside accordion (opening): fade in after expansion has started. */
export const accordionContentOpen = {
  opacity: 0,
};
export const accordionContentOpenAnimate = {
  opacity: 1,
  transition: { delay: 0.1, duration: accordionContentFadeDuration },
};
/** Content fade out (closing): 200ms so swap is smooth before height shrink. */
export const accordionContentClose = {
  opacity: 0,
  transition: { duration: accordionContentFadeDuration },
};
/** Height shrink runs after content swap; 400ms ease-in-out. */
export const accordionCollapseTransition = {
  duration: 0.4,
  ease: [0.42, 0, 0.58, 1] as const,
  delay: accordionContentFadeDuration, // shrink only after fade-out
};

/** Settings Edit Email / Update IBAN: layout morph + content stagger. */
export const settingsAccordionEase = [0.4, 0, 0.2, 1] as const;
/** 300ms ease-out for layout/height so sections below slide smoothly and no jump. */
export const settingsLayoutEaseOut = [0, 0, 0.2, 1] as const;
export const settingsAccordionExpand = {
  duration: 0.25,
  ease: settingsAccordionEase,
};
/** Height and layout transition: 300ms ease-out (dashboard accordion, section slide). */
export const settingsHeightTransition = {
  duration: 0.3,
  ease: settingsLayoutEaseOut,
};
/** Stagger delay (s) between input and Save/Cancel buttons in settings accordion. */
export const SETTINGS_STAGGER_DELAY = 0.05;
/** Fade + slide-up (10px) for settings form content – use with stagger parent. */
export const settingsFormEntry = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25, ease: settingsAccordionEase },
};
export const settingsFormContainerVariants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: SETTINGS_STAGGER_DELAY,
      delayChildren: 0,
    },
  },
};

/** Exit: fade out + scale down (for Cancel on Edit Email / IBAN). */
export const settingsFormExit = {
  opacity: 0,
  scale: 0.98,
  transition: { duration: settingsHeightTransition.duration, ease: settingsHeightTransition.ease },
};
/** Enter: fade in (view row when closing edit). */
export const settingsViewEnter = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: settingsHeightTransition.duration, ease: settingsHeightTransition.ease },
};

/** Stagger (s) between phases in step-by-step accordion. */
export const SETTINGS_PHASE_STAGGER = 0.1;
/** Fade duration (s) for view/form in accordion. */
export const SETTINGS_FADE_DURATION = 0.2;
/** Form enter: fade in + float-in (y: 10 → 0), cubic-bezier(0.4, 0, 0.2, 1). */
export const settingsFormEnter = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0 },
  transition: { duration: SETTINGS_FADE_DURATION, ease: settingsAccordionEase },
};

/** Legacy spring expand (kept for compatibility). Prefer accordionHeightTransition. */
export const springExpand = {
  type: 'spring' as const,
  stiffness: 120,
  damping: 20,
};

/** First element appears after this delay (s) */
export const STAGGER_START_DELAY = 0.1;
/** Delay between each item for short lists (≤ STAGGER_CAP_COUNT) */
export const STAGGER_INTERVAL = 0.04;
/** Lists longer than this get a reduced interval so total stagger window is capped */
export const STAGGER_CAP_COUNT = 10;
/** Max time (s) from first to last item start when list is long */
export const STAGGER_CAP_WINDOW = 0.35;
/** Duration of each item's fade+slide animation (s) */
export const LIST_ITEM_DURATION = 0.25;

/**
 * Stagger delay in seconds for the item at index.
 * First item: STAGGER_START_DELAY. Then +STAGGER_INTERVAL per item, or compressed for long lists.
 */
export function getStaggerDelay(index: number, totalCount: number): number {
  if (totalCount <= 0) return STAGGER_START_DELAY;
  const interval =
    totalCount > STAGGER_CAP_COUNT ? STAGGER_CAP_WINDOW / totalCount : STAGGER_INTERVAL;
  return STAGGER_START_DELAY + index * interval;
}

/**
 * Transition for a single list/table item: delay from getStaggerDelay + snappy duration.
 * Use with listItemEntry initial/animate.
 */
export function getListItemTransition(index: number, totalCount: number) {
  return {
    delay: getStaggerDelay(index, totalCount),
    duration: LIST_ITEM_DURATION,
    ease: easeSnappy,
  };
}

/** Fade + slide entry for list/card/table rows. Use with getListItemTransition(index, totalCount) for transition. */
export const listItemEntry = {
  initial: { opacity: 0, y: 15 },
  animate: { opacity: 1, y: 0 },
};

/** Auth flow: 0.05s between title, content, and action button. Use with listItemEntry. */
export const AUTH_STAGGER_INTERVAL = 0.05;
export function getAuthStaggerTransition(index: number) {
  return {
    delay: index * AUTH_STAGGER_INTERVAL,
    duration: LIST_ITEM_DURATION,
    ease: easeSnappy,
  };
}

export const pageTransition = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: tFast,
};

/** @deprecated Use listItemEntry + getListItemTransition for new lists (conditional on stagger key). */
export const staggerDelay = 0.05;

/** @deprecated Use listItemEntry + getListItemTransition per item. */
export const listStagger = {
  animate: {
    transition: {
      staggerChildren: staggerDelay,
    },
  },
};

/** @deprecated Use listItemEntry. */
export const listItem = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: tFast,
};

export const tapScale = { scale: 0.98 };
export const hoverLift = { y: -2 };
